import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What is the primary focus of socio-relational well-being?",
    options: [
      "Financial stability and career success",
      "Sense of connection, belonging, and support from others",
      "Physical fitness and health metrics",
      "Academic achievement and intellectual growth"
    ],
    correctAnswer: 1,
    explanation: "Socio-relational well-being centers on your sense of connection, belonging, and support from others, encompassing emotional, social, and practical support networks."
  },
  {
    question: "According to attachment theory, which attachment style involves difficulty trusting others and preferring independence?",
    options: [
      "Secure attachment",
      "Anxious attachment",
      "Avoidant attachment",
      "Disorganized attachment"
    ],
    correctAnswer: 2,
    explanation: "Avoidant attachment is characterized by difficulty trusting others and a preference for independence, often stemming from early relationship experiences."
  },
  {
    question: "The MSPSS model measures perceived social support across three main areas. Which of the following is NOT one of them?",
    options: [
      "Significant Other support",
      "Family support",
      "Friends support",
      "Professional support"
    ],
    correctAnswer: 3,
    explanation: "The MSPSS focuses on three specific areas: Significant Other (romantic partner/confidant), Family, and Friends. Professional support is not included in this model."
  },
  {
    question: "Why does perceived social support often matter more than actual support received?",
    options: [
      "Because perception is always more accurate than reality",
      "Because your belief in available support affects how you cope with stress",
      "Because actual support is unreliable",
      "Because perception eliminates the need for real relationships"
    ],
    correctAnswer: 1,
    explanation: "Your perception of available support influences how you handle stress and challenges. Believing you have support can enhance resilience even if you haven't needed to use it recently."
  },
  {
    question: "Which psychological concept explains why social isolation can be as harmful as smoking or obesity?",
    options: [
      "Social bonding theory",
      "Attachment theory",
      "Community psychology",
      "All of the above"
    ],
    correctAnswer: 3,
    explanation: "Social bonding theory, attachment theory, and community psychology all help explain why humans are inherently social creatures and why social isolation poses significant health risks."
  }
];

const SocioRelationalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('socioRelationalM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  React.useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The foundations of socio-relational well-being</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Attachment styles and their impact</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Social support measurement and importance</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your socio-relational wellness journey by building stronger connections!</p>
            <p>Ready to deepen your understanding of communication skills?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/socio-relational-resources')}
          >
            Return to Socio-Relational Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left socio-relational-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Socio-Relational Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/socio-relational-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule1QuizPage;

